import { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import { DeviceType } from 'shared/models/device-type';

import { OrganisationFormName } from 'features/organisation/domain/enums/organisation-form-name';
import AlternativeDiversionOrganisationUnit from 'features/organisation/domain/models/alternative-diversion-organisation-unit';
import EmergencyAlarmRouteOrganisationUnit from 'features/organisation/domain/models/emergency-alarm-route-organisation-unit';
import { useLazyGetOrganisationUnit } from 'features/organisation/domain/queries/organisation-unit-queries';
import OrganisationRoomLink from 'features/organisation/views/organisation-details/components/electronic-client-record/domain/organisation-room-link';
import OrganisationUnit from '../domain/models/organisation-unit';
import OrganisationUnitDevice from '../domain/models/organisation-unit-device';
import SubscriptionChannelValidator from '../domain/validators/subscription-channel-validator';
import Scenario from '../models/scenario';

type OrganisationUnitDetailForm = {
  [OrganisationFormName.Name]: string;
  [OrganisationFormName.IsLocation]: boolean;
  [OrganisationFormName.ResidentialLocation]: boolean;
  [OrganisationFormName.IsSubscriptionChannel]: boolean;
  [OrganisationFormName.IsHidden]: boolean;
  [OrganisationFormName.DiversionTimeout]: number;
  [OrganisationFormName.DiversionTimeoutEnabled]: boolean;
  [OrganisationFormName.Devices]: OrganisationUnitDevice[];
  [OrganisationFormName.Scenarios]: Scenario[];
  [OrganisationFormName.IntentionOutOfBedEnabled]: boolean;
  [OrganisationFormName.AlternativeDiversionEnabled]: boolean;
  [OrganisationFormName.AlternativeDiversionOrganisationUnits]: AlternativeDiversionOrganisationUnit[];
  [OrganisationFormName.EmergencyAlarmRoutesEnabled]: boolean;
  [OrganisationFormName.EmergencyAlarmRouteOrganisationUnits]: EmergencyAlarmRouteOrganisationUnit[];
  [OrganisationFormName.ElectronicClientRecordRoom]: OrganisationRoomLink;
  [OrganisationFormName.TransmitterResidentSettings]: DeviceType[];
};

export interface IOrganisationUnitDetailsHook {
  form: UseFormReturn<OrganisationUnitDetailForm>;
  defaultFormValues: OrganisationUnitDetailForm;
  selectedOrganisationUnit: OrganisationUnit | undefined;
  isLoadingSelectedOrganisationUnit: boolean;
  loadOrganisationUnit: (organisationUnitId: string) => void;
  refresh: () => void;

  getDiversionTimeoutSelectValueOrDefault: (value: number | undefined) => number;
  organisationUnitTreeBranchContainsLocation: (
    selectedNode: OrganisationTreeNode | undefined,
    parentNode: OrganisationTreeNode | undefined,
    upstreamParentBranch: OrganisationTreeNode[],
  ) => boolean;
  isSelectedScenario: (scenario: Scenario, scenarios: Scenario[]) => boolean;
  isSelectedDevice: (scenario: OrganisationUnitDevice, scenarios: OrganisationUnitDevice[]) => boolean;
  isSelectedAlternativeDiversion: (
    alternativeDiversion: AlternativeDiversionOrganisationUnit,
    alternativeDiversions: AlternativeDiversionOrganisationUnit[],
  ) => boolean;
  isScrolling: boolean;
  handleScroll: (event: React.UIEvent<HTMLElement>) => void;
  getParentLocation(
    selectedNode: OrganisationTreeNode | undefined,
    upstreamParentBranch: OrganisationTreeNode[],
  ): OrganisationTreeNode | undefined;
}

const useOrganisationUnitDetails = (): IOrganisationUnitDetailsHook => {
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  const defaultFormValues = {
    [OrganisationFormName.Name]: '',
    [OrganisationFormName.IsLocation]: false,
    [OrganisationFormName.ResidentialLocation]: false,
    [OrganisationFormName.IsSubscriptionChannel]: false,
    [OrganisationFormName.IsHidden]: false,
    [OrganisationFormName.DiversionTimeout]: 900,
    [OrganisationFormName.DiversionTimeoutEnabled]: false,
    [OrganisationFormName.Devices]: [] as OrganisationUnitDevice[],
    [OrganisationFormName.Scenarios]: [] as Scenario[],
    [OrganisationFormName.IntentionOutOfBedEnabled]: false,
    [OrganisationFormName.AlternativeDiversionEnabled]: false,
    [OrganisationFormName.AlternativeDiversionOrganisationUnits]: [] as AlternativeDiversionOrganisationUnit[],
    [OrganisationFormName.EmergencyAlarmRoutesEnabled]: false,
    [OrganisationFormName.EmergencyAlarmRouteOrganisationUnits]: [] as EmergencyAlarmRouteOrganisationUnit[],
    [OrganisationFormName.ElectronicClientRecordRoom]: {} as unknown as OrganisationRoomLink,
    [OrganisationFormName.TransmitterResidentSettings]: [] as DeviceType[],
  };

  const form = useForm({
    mode: 'onBlur',
    defaultValues: defaultFormValues,
  });
  const [organisationId, setOrganisationId] = useState('');
  const {
    refetch: triggerGetOrganisationUnit,
    data: getOrganisationUnitData,
    isFetching: getOrganisationUnitIsFetching,
    isLoading: getOrganisationUnitIsLoading,
  } = useLazyGetOrganisationUnit(organisationId);

  const refresh = (): void => {
    if (getOrganisationUnitData && organisationId !== '') {
      triggerGetOrganisationUnit();
    }
  };

  useEffect(() => {
    if (organisationId !== '') {
      triggerGetOrganisationUnit();
    }
  }, [organisationId]);

  const loadOrganisationUnit = (organisationUnitId: string): void => {
    if (organisationId === organisationUnitId) {
      triggerGetOrganisationUnit();
    } else {
      setOrganisationId(organisationUnitId);
    }
  };

  const getDiversionTimeoutSelectValueOrDefault = (value: number | undefined): number => {
    const possibleValues = [900, 1800, 2700];
    if (value && possibleValues.find((x) => x === value)) {
      return value;
    }

    return possibleValues[0];
  };

  const organisationUnitTreeBranchContainsLocation = (
    selectedNode: OrganisationTreeNode | undefined,
    parentNode: OrganisationTreeNode | undefined,
    upstreamParentBranch: OrganisationTreeNode[],
  ): boolean => {
    if (selectedNode?.isLocation) {
      return false;
    }

    if (
      SubscriptionChannelValidator.hasLocationInParents(parentNode?.id, upstreamParentBranch) ||
      SubscriptionChannelValidator.hasLocationInChildren(selectedNode?.childOrganisationUnits)
    ) {
      return true;
    }
    return false;
  };

  const getParentLocation = (
    selectedNode: OrganisationTreeNode | undefined,
    upstreamParentBranch: OrganisationTreeNode[],
  ): OrganisationTreeNode | undefined => {
    if (selectedNode?.isLocation) {
      return selectedNode;
    }

    return upstreamParentBranch.find((upstreamParent) => upstreamParent.isLocation);
  };

  const isSelectedScenario = (scenario: Scenario, selectedScenarios: Scenario[]): boolean => {
    return selectedScenarios.findIndex((selectedScenario) => selectedScenario.id === scenario.id) === -1;
  };

  const isSelectedDevice = (device: OrganisationUnitDevice, selectedDevices: OrganisationUnitDevice[]): boolean => {
    return selectedDevices.findIndex((selectedDevice) => selectedDevice.id === device.id) === -1;
  };

  const isSelectedAlternativeDiversion = (
    alternativeDiversion: AlternativeDiversionOrganisationUnit,
    alternativeDiversions: AlternativeDiversionOrganisationUnit[],
  ): boolean =>
    alternativeDiversions.findIndex((selectedAlternativeDiversion) => selectedAlternativeDiversion.id === alternativeDiversion.id) === -1;

  const handleScroll = (event: React.UIEvent<HTMLElement>): void => {
    if (isScrolling && event.currentTarget.scrollTop === 0) {
      setIsScrolling(false);
    } else if (!isScrolling && event.currentTarget.scrollTop > 0) {
      setIsScrolling(true);
    }
  };

  return {
    form,
    defaultFormValues,
    selectedOrganisationUnit: getOrganisationUnitData,
    isLoadingSelectedOrganisationUnit: getOrganisationUnitIsFetching || getOrganisationUnitIsLoading,
    loadOrganisationUnit,
    refresh,

    getDiversionTimeoutSelectValueOrDefault,
    organisationUnitTreeBranchContainsLocation,
    isSelectedScenario,
    isSelectedDevice,
    isSelectedAlternativeDiversion,
    isScrolling,
    handleScroll,
    getParentLocation,
  };
};

export default useOrganisationUnitDetails;
